<template>
    <v-card>
        <v-card-title>
            <v-row justify="space-between">
                <v-col cols="2"> Viewing Files for {{ experimentCode }}</v-col>
                <v-spacer />
                <v-col cols="10" class="text-right">
                    <v-btn color="primary" @click="openFileDialogForUpload">Upload File</v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-card-text>
            <FilesList
                :files="files"
                :search="search"
                @deleteFile="deleteFile"
                @editFile="openFileDialogForEditing"
            />
        </v-card-text>
    </v-card>
    <v-dialog v-model="showFileDialog" max-width="600px">
        <v-card>
            <v-card-title class="text-h5 bg-grey-lighten-2" primary-title style="display: relative">
                {{ dialogTitle }}
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            v-model="fileCategory"
                            :items="fileCategories"
                            label="File Category"
                            required
                            :rules="[(value) => !!value || 'File category is required']"
                            return-object
                            v-on:update:model-value="setDefaultVisibility"
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                        <v-checkbox
                            v-model="fileVisibility"
                            label="Internal only"
                            :true-value="'Internal'"
                            :false-value="'Public'"
                        ></v-checkbox>
                    </v-col>
                </v-row>
                <v-row v-if="!fileForEditing">
                    <v-file-input
                        v-model="fileForUpload"
                        accept=".xlsx,.pdf"
                        label="Select a file"
                    ></v-file-input>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="closeFileDialog" color="primary">Cancel</v-btn>
                <v-btn
                    v-if="!fileForEditing"
                    @click="uploadFile"
                    :disabled="!fileForUpload"
                    color="primary"
                    >Upload</v-btn
                >
                <v-btn v-else @click="editFile" :disabled="!fileForEditing" color="primary"
                    >Save Changes</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import FilesList from "@/components/Files/FilesList.vue";
import { ExperimentFile, filesAPI } from "@/services/files/filesAPI";
import { S3Uploader } from "@/lib/s3Uploader";
import { useAuth } from "@/lib/ts/auth";
import config from "@/config";
import { withLoadingScreen } from "@/lib/loading";

const auth = useAuth();

const authUser = computed(() => {
    return auth.getAuthUser();
});

interface Props {
    experimentCode: string;
    search: string;
}

const props = withDefaults(defineProps<Props>(), {
    experimentCode: "",
    search: "",
});

const files = ref<ExperimentFile[]>([]);
const showFileDialog = ref(false);
const fileForEditing = ref<ExperimentFile | null>(null);
const fileForUpload = ref<File | null>(null);
const fileCategory = ref<string | null>(null);
const fileCategories = ref<string[]>(["SOW", "SOP", "Final Report", "Other"]);
const fileVisibility = ref<string | null>(null);
fileVisibility.value = "Public";

onMounted(async () => {
    await reloadFiles();
});

async function reloadFiles() {
    files.value = await filesAPI.fetchExperimentFiles(props.experimentCode);
}

async function uploadFile() {
    if (doesFileAlreadyExist(fileForUpload.value?.name || "")) {
        alert(
            `File ${fileForUpload.value?.name} already exists. Please rename the file and try again.`
        );
        return;
    }
    withLoadingScreen(async () => {
        if (fileForUpload.value) {
            await auth.refreshSession();
            // upload file to S3
            const s3Uploader = new S3Uploader(authUser.value.idToken);
            const filename = `${props.experimentCode}/${fileForUpload.value.name}`;
            await s3Uploader.uploadFile(
                config.experimentFilesBucket,
                filename,
                fileForUpload.value,
                fileForUpload.value.type
            );

            // tell the backend that we uploaded a file
            await filesAPI.uploadExperimentFile(
                props.experimentCode,
                fileForUpload.value.name,
                authUser.value.email,
                fileVisibility.value,
                fileCategory.value
            );

            fileForUpload.value = null;
            fileCategory.value = null;
            fileVisibility.value = null;
            showFileDialog.value = false;
            await reloadFiles();
        }
    });
}

async function editFile() {
    if (fileForEditing.value) {
        withLoadingScreen(async () => {
            await filesAPI.updateExperimentFile(
                props.experimentCode,
                fileForEditing.value!.id,
                fileCategory.value,
                fileVisibility.value
            );

            fileForEditing.value = null;
            fileCategory.value = null;
            showFileDialog.value = false;
            await reloadFiles();
        });
    }
}

function openFileDialogForUpload() {
    fileForEditing.value = null;
    fileVisibility.value = "Public";
    showFileDialog.value = true;
}

function closeFileDialog() {
    showFileDialog.value = false;
    fileForUpload.value = null;
}

function deleteFile(file: ExperimentFile) {
    if (confirm(`Are you sure you want to delete file ${file.filename}?`)) {
        withLoadingScreen(async () => {
            await filesAPI.deleteExperimentFile(file.experimentCode, file.id);
            await reloadFiles();
        });
    }
}

function openFileDialogForEditing(file: ExperimentFile) {
    fileForEditing.value = file;
    showFileDialog.value = true;
    fileCategory.value = file.category;
    fileVisibility.value = file.visibility;
}

function setDefaultVisibility() {
    if (fileCategory.value === "Final Report") {
        fileVisibility.value = "Internal";
    }
}

function doesFileAlreadyExist(fileName: string) {
    return files.value.some((file) => file.filename === fileName);
}

const dialogTitle = computed(() => {
    return fileForEditing.value
        ? `Edit File ${fileForEditing.value.filename}`
        : `Upload File for ${props.experimentCode}`;
});
</script>
